<!-- Create New Role -->
<div class="create-role-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <button class="btn btn-outline-secondary btn-sm" style="margin-right: 8px;" (click)="router.navigateByUrl('/admin/roles_and_permissions')">
          <i class="fas fa-arrow-left"></i>
        </button>
        <div class="header-icon">
          <i class="fas fa-plus-circle"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Create New Role</h1>
          <p class="page-description">Define a new role and assign permissions for system access</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline-secondary" (click)="router.navigateByUrl('/admin/roles_and_permissions')">
          <i class="fas fa-times me-1"></i>Cancel
        </button>
        <button class="btn btn-primary" (click)="update()" [disabled]="isCreatingRole">
          <span *ngIf="!isCreatingRole">
            <i class="fas fa-save me-1"></i>Save Role
          </span>
          <span *ngIf="isCreatingRole">
            <i class="fas fa-spinner fa-spin me-1"></i>Saving...
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="row g-4">
    <!-- Main Form Column -->
    <div class="col-lg-8">
      <!-- Role Information Card -->
      <div class="data-panel" style="margin-bottom: 20px;">
        <div class="panel-header">
          <div class="panel-title">
            <div class="title-icon">
              <i class="fas fa-user-tag"></i>
            </div>
            <div>
              <div class="title-text">Role Information</div>
              <div class="title-subtitle">Basic details about the new role</div>
            </div>
          </div>
        </div>
        <div class="panel-body" style="padding: 20px;">
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-tag me-2" style="color: var(--accent);"></i>
              Role Name <span style="color: var(--danger-text);">*</span>
            </label>
            <input type="text"
                   class="form-control"
                   name="name"
                   [(ngModel)]="name"
                   placeholder="Enter role name (e.g., Manager, Staff, Admin)"
                   required>
            <small style="color: var(--text-tertiary); font-size: 11px; margin-top: 4px; display: block;">Choose a clear, descriptive name for this role</small>
          </div>

          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label">
              <i class="fas fa-align-left me-2" style="color: var(--accent);"></i>
              Description
            </label>
            <textarea class="form-control"
                      name="description"
                      [(ngModel)]="description"
                      rows="3"
                      placeholder="Describe the purpose and responsibilities of this role (optional)"></textarea>
            <small style="color: var(--text-tertiary); font-size: 11px; margin-top: 4px; display: block;">Provide a brief description to help others understand this role's purpose</small>
          </div>
        </div>
      </div>

      <!-- Permissions Card -->
      <div class="data-panel">
        <div class="panel-header">
          <div class="panel-title">
            <div class="title-icon">
              <i class="fas fa-key"></i>
            </div>
            <div>
              <div class="title-text">Permissions</div>
              <div class="title-subtitle">Configure what this role can access and do</div>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge badge-soft-primary">
              {{ selectedPermissions.length }} of {{ permissions?.length || 0 }} selected
            </span>
          </div>
        </div>

        <!-- Permissions Body - Loaded -->
        <div class="panel-body" style="padding: 20px;" *ngIf="!isLoadingPermissions">
          <!-- Select All / Clear All Controls -->
          <div class="d-flex align-items-center gap-2" style="margin-bottom: 16px;">
            <button class="btn btn-sm btn-secondary" type="button" (click)="selectAllPermissions()">
              <i class="fas fa-check-double me-1"></i>Select All
            </button>
            <button class="btn btn-sm btn-outline-secondary" type="button" (click)="clearAllPermissions()">
              <i class="fas fa-times me-1"></i>Clear All
            </button>
          </div>

          <!-- Permissions Grid -->
          <div style="display: grid; gap: 8px;">
            <div *ngFor="let perm of permissions; trackBy: trackByPermissionId"
                 style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: var(--bg-muted); border-radius: var(--radius-md); border: 1px solid var(--border-subtle); transition: all var(--transition);">
              <div class="d-flex align-items-center gap-3">
                <div style="width: 28px; height: 28px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 11px;">
                  <i class="fas fa-shield-alt"></i>
                </div>
                <div>
                  <div style="font-size: 13px; font-weight: 500; color: var(--text-primary);">{{ formatPermission(perm?.name) }}</div>
                  <div style="font-size: 11px; color: var(--text-tertiary);">{{ perm?.name }}</div>
                </div>
              </div>
              <div class="form-check form-switch">
                <input class="form-check-input"
                       type="checkbox"
                       role="switch"
                       [id]="'perm-' + perm?.id"
                       [checked]="isChecked(perm?.id)"
                       (change)="onCheckboxChange($event, perm?.name)">
                <label class="form-check-label" [for]="'perm-' + perm?.id"></label>
              </div>
            </div>
          </div>
        </div>

        <!-- Permissions Body - Loading Shimmer -->
        <div class="panel-body" style="padding: 20px;" *ngIf="isLoadingPermissions">
          <div style="display: grid; gap: 8px;">
            <div *ngFor="let i of [1,2,3,4,5,6]"
                 style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: var(--bg-muted); border-radius: var(--radius-md);">
              <div class="d-flex align-items-center gap-3">
                <div class="loading-shimmer" style="width: 28px; height: 28px; border-radius: var(--radius-sm);"></div>
                <div>
                  <div class="loading-shimmer" style="width: 180px; height: 14px; margin-bottom: 4px; border-radius: 4px;"></div>
                  <div class="loading-shimmer" style="width: 120px; height: 10px; border-radius: 4px;"></div>
                </div>
              </div>
              <div class="loading-shimmer" style="width: 44px; height: 24px; border-radius: 12px;"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sidebar Column -->
    <div class="col-lg-4">
      <!-- Role Preview Card -->
      <div class="data-panel" style="margin-bottom: 20px; position: sticky; top: 20px;">
        <div class="panel-header">
          <div class="panel-title">
            <div class="title-icon">
              <i class="fas fa-eye"></i>
            </div>
            <div>
              <div class="title-text">Role Preview</div>
              <div class="title-subtitle">Review your role configuration</div>
            </div>
          </div>
        </div>
        <div class="panel-body" style="padding: 20px;">
          <!-- Role Name Preview -->
          <div style="margin-bottom: 20px;">
            <div style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); margin-bottom: 6px; display: flex; align-items: center; gap: 6px;">
              <i class="fas fa-tag" style="font-size: 10px;"></i>Role Name
            </div>
            <div style="font-size: 14px; font-weight: 500; color: var(--text-primary); padding: 8px 12px; background: var(--bg-muted); border-radius: var(--radius-sm); border: 1px solid var(--border-subtle);">
              {{ name || 'Enter role name' }}
            </div>
          </div>

          <!-- Description Preview -->
          <div style="margin-bottom: 20px;">
            <div style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); margin-bottom: 6px; display: flex; align-items: center; gap: 6px;">
              <i class="fas fa-align-left" style="font-size: 10px;"></i>Description
            </div>
            <div style="font-size: 13px; color: var(--text-secondary); padding: 8px 12px; background: var(--bg-muted); border-radius: var(--radius-sm); border: 1px solid var(--border-subtle);">
              {{ description || 'No description provided' }}
            </div>
          </div>

          <!-- Permissions Preview -->
          <div>
            <div style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); margin-bottom: 6px; display: flex; align-items: center; gap: 6px;">
              <i class="fas fa-key" style="font-size: 10px;"></i>Permissions
            </div>
            <div style="padding: 8px 12px; background: var(--bg-muted); border-radius: var(--radius-sm); border: 1px solid var(--border-subtle);">
              <span class="badge badge-soft-primary" style="margin-bottom: 8px;">
                {{ selectedPermissions.length }} permission(s) selected
              </span>
              <div *ngIf="selectedPermissions.length > 0" style="display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;">
                <span class="badge badge-light" style="font-size: 11px;" *ngFor="let perm of getSelectedPermissionNames(); let i = index">
                  {{ perm }}
                </span>
                <span class="badge badge-soft-secondary" style="font-size: 11px;" *ngIf="selectedPermissions.length > 3">
                  +{{ selectedPermissions.length - 3 }} more
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Help / Tips Card -->
      <div class="data-panel">
        <div class="panel-header">
          <div class="panel-title">
            <div class="title-icon" style="background: var(--info-bg); color: var(--info-text);">
              <i class="fas fa-question-circle"></i>
            </div>
            <div>
              <div class="title-text">Need Help?</div>
              <div class="title-subtitle">Tips for creating roles</div>
            </div>
          </div>
        </div>
        <div class="panel-body" style="padding: 20px;">
          <div style="display: grid; gap: 16px;">
            <div class="d-flex gap-3">
              <div style="width: 28px; height: 28px; border-radius: var(--radius-sm); background: var(--warning-bg); color: var(--warning-text); display: flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0;">
                <i class="fas fa-lightbulb"></i>
              </div>
              <div>
                <div style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px;">Role Naming</div>
                <div style="font-size: 12px; color: var(--text-tertiary); line-height: 1.5;">Use clear, descriptive names like "Manager", "Cashier", or "Administrator".</div>
              </div>
            </div>

            <div class="d-flex gap-3">
              <div style="width: 28px; height: 28px; border-radius: var(--radius-sm); background: var(--success-bg); color: var(--success-text); display: flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0;">
                <i class="fas fa-shield-alt"></i>
              </div>
              <div>
                <div style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px;">Permission Guidelines</div>
                <div style="font-size: 12px; color: var(--text-tertiary); line-height: 1.5;">Only assign permissions that are necessary for the role's responsibilities.</div>
              </div>
            </div>

            <div class="d-flex gap-3">
              <div style="width: 28px; height: 28px; border-radius: var(--radius-sm); background: var(--info-bg); color: var(--info-text); display: flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0;">
                <i class="fas fa-users"></i>
              </div>
              <div>
                <div style="font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px;">Best Practices</div>
                <div style="font-size: 12px; color: var(--text-tertiary); line-height: 1.5;">Create specific roles for different job functions to maintain security.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
